import React, { lazy } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import env from 'Env';
import Meta from 'Components/Utility/SEO/Meta';
import useViewport from 'Hooks/useViewport';
import FeaturedCarouselContainer from 'Components/Carousel/FeaturedCarouselContainer';
import FlashDealsSection from 'Components/AlgoliaSections/FlashDealsSection';
import TakeOver from 'Components/Banner/TakeOver';
import TopSellersTop from 'Features/Home/components/TopSellersTop';
import TrustBanner from 'Components/Banner/TrustBanner/TrustBanner';
import MysteryBundleCardContainer from 'Components/Cards/MysteryBundleCardContainer';
import LazyOnVisible from 'Components/Utility/LazyOnVisible/LazyOnVisible';
import messages from 'Features/Home/helpers/home-messages';
import useFetchHomeHits from 'Features/Home/hooks/useFetchHomeHits';

const BundleFestCountdown = lazy(() => import(/* webpackChunkName: "BlogPosts" */ 'Components/Banner/BundleFestCountdown/BundleFestCountdown'));
const LimitedTimeDealsCarousel = lazy(() => import(/* webpackChunkName: "LimitedTimeDealsCarousel" */ 'Components/Carousel/LimitedTimeDealsCarousel/LimitedTimeDealsCarousel'));
const RecentlyViewedSection = lazy(() => import(/* webpackChunkName: "RecentlyViewed" */ 'Components/AlgoliaSections/RecentlyViewedSection'));
const WishlistMostWantedSection = lazy(() => import(/* webpackChunkName: "WishlistMostWantedSection" */ 'Components/Wishlist/WishlistMostWantedSection'));
const FanFavouritesSection = lazy(() => import(/* webpackChunkName: "FanFavouritesSection" */ 'Components/FanFavourites/FanFavouritesSection'));
const PublisherCarousel = lazy(() => import(/* webpackChunkName: "PublisherCarousel" */ 'Components/Carousel/PublisherCarousel/PublisherCarousel'));
const ProductFinder = lazy(() => import(/* webpackChunkName: "ProductFinder" */ 'Components/ProductFinder/ProductFinder'));
const ProductCardCarouselSection = lazy(() => import(/* webpackChunkName: "ProductCardCarouselSection" */ 'Components/Carousel/ProductCardCarousel/ProductCardCarouselSection'));
const PopularCategories = lazy(() => import(/* webpackChunkName: "PopularCategoriesTabs" */ 'Features/Home/components/PopularCategories'));
const QuickLinks = lazy(() => import(/* webpackChunkName: "QuickLinks" */ 'Components/QuickLinks/QuickLinks'));
const MidPageBannerContainer = lazy(() => import(/* webpackChunkName: "MidPageBannerContainer" */ 'Components/Banner/MidPageBanner/MidPageBannerContainer'));
const MoreThanJustGames = lazy(() => import(/* webpackChunkName: "MoreThanJustGames" */ 'Features/Home/components/MoreThanJustGames'));
const EmailSignupPlacement = lazy(() => import(/* webpackChunkName: "EmailSignupPlacement" */ 'Components/Banner/EmailSignupPlacement/EmailSignupPlacement'));

const HomePage = () => {
  const { isAboveSm, isAboveMd, isBelowLg } = useViewport();
  const { formatMessage } = useIntl();
  const siteBrand = useSelector(state => state.siteBrand) || 'fanatical';
  const userLibrary = useSelector(state => state.userLibrary) || [];
  const steamLibrary = useSelector(state => state.steamLibrary) || [];
  const starDeal = useSelector(state => state.starDeal) || null;

  const { apiResults } = useFetchHomeHits();

  const liveSales = useSelector(state => state.sales.liveSales);
  const isSaleLive = !!liveSales.find(sale => sale.majorSale);

  let topSellers = apiResults?.topSellers || [];

  const {
    newReleases: newReleaseHits = [],
    trendingDeals: trendingHits = [],
    mostWished: mostWishedHits = [],
    gameBundles = [],
    mysteryBundles = [],
    fantasyverse: fantasyVerseHits = [],
    games: gameHits = [],
    booksComics: booksComicsHits = [],
    other: otherHits = [],
    booksStandard: booksStandardHits = [],
    software: softwareHits = [],
    elearning: elearningHits = [],
    audio: audioHits = [],
    giveaways = [],
    comingSoon: comingSoonHits = [],
    gamesUnderFive: gamesUnderFiveHits = [],
  } = apiResults || {};

  // Combine fanatical owned and steam owned
  const purchaseHistoryNames = [...userLibrary, ...steamLibrary];


  // stops the test launching for no reason
  // we should only launch the test if the user owns
  // one of the first 11 products that we display on the homepage
  const hasPurchased = !!topSellers
    .slice(0, 11)
    .find(hit => purchaseHistoryNames.includes(hit.name));

  const shiftPurchasedTopSellersToBack = (hits) => {
    const unPurchasedTopSellers = hits.filter(hit => !purchaseHistoryNames.includes(hit.name));
    const purchasedTopSellers = hits.filter(hit => purchaseHistoryNames.includes(hit.name));

    return [...unPurchasedTopSellers, ...purchasedTopSellers];
  };

  if (hasPurchased) {
    topSellers = shiftPurchasedTopSellersToBack(topSellers);
  }

  const topSellerSlugs = [];
  topSellers.slice(0, 11).forEach((hit) => { topSellerSlugs.push(hit.slug); });

  // Get game bundles not in the top sellers
  const moreGameBundles = gameBundles.filter(hit => !topSellerSlugs.includes(hit.slug));
  const showLimitedTimeDeals = false;

  return (
    <React.Fragment>
      <Meta
        url="/"
        hits={topSellers}
        title={`Fanatical | ${formatMessage(messages.title)}`}
        description={formatMessage(messages.desc)}
        pageType="CollectionPage"
      />
      <TakeOver appearsOn="home" />

      <main className="homepage HomePage">
        <section className="container">
          <FeaturedCarouselContainer />
        </section>

        {isAboveSm && <TrustBanner />}

        <FlashDealsSection />

        <BundleFestCountdown />

        <MysteryBundleCardContainer />

        <TopSellersTop
          hits={topSellers}
          gameHits={gameHits}
          gameBundles={gameBundles}
          booksComicsHits={booksComicsHits}
          otherHits={otherHits}
        />

        {showLimitedTimeDeals &&
          <LazyOnVisible>
            <LimitedTimeDealsCarousel
              gameHits={gameHits}
              starDeal={starDeal}
              gameBundles={gameBundles}
              mysteryBundles={mysteryBundles}
              giveaways={giveaways}
            />
          </LazyOnVisible>
        }

        {isAboveMd &&
          <LazyOnVisible>
            <ProductCardCarouselSection
              title={formatMessage(messages.bundles)}
              viewAllLocation="bundle"
              loadedHits={moreGameBundles}
              viewAllTitle={formatMessage(messages.bundles)}
              hitCardNameTag="h3"
              slidesToShow={3}
              lgSize={4}
            />
          </LazyOnVisible>
        }

        <LazyOnVisible>
          <EmailSignupPlacement
            needsContainer
            appearsOn="home"
          />
        </LazyOnVisible>

        <LazyOnVisible>
          <MoreThanJustGames
            fantasyVerseHits={fantasyVerseHits}
            booksStandardHits={booksStandardHits}
            softwareHits={softwareHits}
            elearningHits={elearningHits}
            audioHits={audioHits}
          />
        </LazyOnVisible>

        <MidPageBannerContainer lazyLoad appearsOn="home" />

        {isBelowLg &&
          <LazyOnVisible>
            <ProductCardCarouselSection
              title={formatMessage(messages.newandupcoming)}
              viewAllLocation="upcoming-games"
              loadedHits={newReleaseHits}
              viewAllTitle={formatMessage(messages.newandupcoming)}
              hitCardNameTag="h3"
            />
          </LazyOnVisible>
        }

        <LazyOnVisible>
          <QuickLinks />
        </LazyOnVisible>

        {isAboveMd &&
          <LazyOnVisible>
            <PopularCategories
              trendingHits={trendingHits}
              newReleaseHits={newReleaseHits}
              mostWishedHits={mostWishedHits}
              comingSoonHits={comingSoonHits}
              gamesUnderFiveHits={gamesUnderFiveHits}
            />
          </LazyOnVisible>
        }

        {isBelowLg &&
          <LazyOnVisible>
            <ProductCardCarouselSection
              viewAllLocation="trending-deals"
              loadedHits={trendingHits}
              viewAllTitle={formatMessage(messages.trending_deals)}
              hitCardNameTag="h3"
              title={formatMessage(messages.trending_deals)}
            />
          </LazyOnVisible>
        }

        <LazyOnVisible containerClass="ProductFinder" >
          <ProductFinder />
        </LazyOnVisible>

        {env.homePublishers &&
          <LazyOnVisible>
            <PublisherCarousel showDealNumbers />
          </LazyOnVisible>
        }

        <LazyOnVisible>
          <FanFavouritesSection />
        </LazyOnVisible>

        {isBelowLg && siteBrand === 'fanatical' && isSaleLive &&
          <LazyOnVisible>
            <WishlistMostWantedSection
              loadedHits={mostWishedHits}
            />
          </LazyOnVisible>
        }

        {env.homeRecentlyViewed &&
          <LazyOnVisible>
            <RecentlyViewedSection
              hitCardNameTag="h3"
            />
          </LazyOnVisible>
        }
      </main>
    </React.Fragment>
  );
};

export default HomePage;
